import { gql, useMutation, useQuery } from "@apollo/client";
import { faBars, faShoppingCart, faTimes, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useApplicationContext } from "components/Application";
import { useOnClickOutside } from "hooks/useOnClickOutside";
import { capitalize, filter } from "lodash";
import numeral from "numeral";
import React, { Fragment, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import tw from "twin.macro";

import "react-toastify/dist/ReactToastify.css";

numeral.register("locale", "it", {
  delimiters: {
    thousands: ",",
    decimal: ",",
  },
  abbreviations: {
    thousand: "k",
    million: "m",
    billion: "b",
    trillion: "t",
  },
  ordinal: function (number) {
    return number === 1 ? "er" : "ème";
  },
  currency: {
    symbol: "€",
  },
});
numeral.locale("it");

const stopImpersonatingAccountUserMutation = gql`
  mutation {
    stopImpersonatingAccountUser {
      successful
    }
  }
`;

export const MainLayout: React.FC<{}> = () => {
  const { session, clearSessionToken, refetchSession } = useApplicationContext();
  const { user, currentUser } = session;
  const { brands } = currentUser;

  const orderDocumentQuery = gql`
    query OrderDocument {
      accountSession {
        currentUser {
          cartDocument {
            id
            rows {
              id
            }
          }
        }
      }
    }
  `;

  const { data } = useQuery(orderDocumentQuery);
  let cartIsEmpty = data?.accountSession?.currentUser?.cartDocument?.rows?.length === 0;

  const cartPathMatch = useMatch("/cart");
  const profilePathMatch = useMatch("/profile");
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { i18n } = useTranslation();
  const languageSuffix = capitalize(i18n.language);

  const refHandler = useRef(null);
  const refCollapsable = useRef(null);
  const [navDropdownOpened, setNavOpened] = useState<false | string>(false);
  const [mobileMenuOpened, setMobileMenuOpened] = useState<false | string>(false);
  useOnClickOutside([refHandler, refCollapsable], () => setNavOpened(false));
  useOnClickOutside([refHandler, refCollapsable], () => setMobileMenuOpened(false));

  const [execMutation] = useMutation(stopImpersonatingAccountUserMutation, {
    errorPolicy: "none",
    onCompleted: (data) => {
      refetchSession();
      navigate("/");
    },
  });

  const signOutImporsonator = () => execMutation();
  const signOutUser = () => clearSessionToken();

  let visibleBrands = brands;
  if (user?.role === "SALESPERSON" && user?.id !== currentUser?.id) {
    visibleBrands = [];
    const { userBrands } = currentUser;
    brands?.forEach((brand: any) => {
      userBrands?.forEach((userBrand: any) => {
        if (userBrand?.brand?.id === brand?.id && userBrand?.salesperson?.id === user?.id) visibleBrands?.push(brand);
      });
    });
  }

  return (
    <Fragment>
      <div tw="min-h-screen w-screen">
        {mobileMenuOpened && (
          <div tw="absolute w-screen overflow-scroll h-screen z-10 pt-20 pb-40 bg-black">
            <div
              key="close"
              tw="cursor-pointer fixed text-2xl z-20"
              style={{ top: "1rem", left: "2rem" }}
              onClick={() => {
                setMobileMenuOpened(false);
              }}
            >
              <FontAwesomeIcon tw="text-white" icon={faTimes} />
            </div>
            <div tw="flex flex-col h-full w-full h-full justify-center">
              {visibleBrands.map((brand: any) => {
                const currentSeasons: any = filter(brand.seasons, { isEnabled: true, isStock: false });
                const stockSeasons: any = filter(brand.seasons, { isEnabled: true, isStock: true });
                const hasStockSeasons = stockSeasons.length > 0;

                return (
                  <div tw="flex flex-col items-center mb-12" key={`${brand.id}-group-responsive`}>
                    {currentSeasons.length > 0 && (
                      <Fragment>
                        <span tw="text-xl text-white mb-4">{brand[`name${languageSuffix}`].toUpperCase()} </span>
                        {currentSeasons.map((season: any) => (
                          <Link
                            key={`${brand.id}-${season.id}-responsive`}
                            to={`/b/${brand.id}/s/${season.id}`}
                            onClick={() => {
                              setMobileMenuOpened(false);
                            }}
                            css={[
                              tw`mb-2 outline-none text-base text-gray-500 border-b-2 border-transparent`,
                              pathname.includes(brand.id) &&
                                pathname.includes(season.id) &&
                                tw`text-white border-white`,
                            ]}
                          >
                            {season[`name${languageSuffix}`].toUpperCase()}{" "}
                            {season[`kind${languageSuffix}`].toUpperCase()}
                          </Link>
                        ))}
                      </Fragment>
                    )}
                    {hasStockSeasons && (
                      <Fragment>
                        {stockSeasons.map((season: any) => (
                          <Link
                            key={`${brand.id}-${season.id}-stock-responsive`}
                            to={`/b/${brand.id}/s/${season.id}`}
                            onClick={() => {
                              setMobileMenuOpened(false);
                            }}
                            css={[
                              tw`mb-2 outline-none text-base text-gray-500 border-b-2 border-transparent`,
                              pathname.includes(brand.id) &&
                                pathname.includes(season.id) &&
                                tw`text-white border-white`,
                            ]}
                          >
                            {stockSeasons[0][`name${languageSuffix}`].toUpperCase()}{" "}
                            {season[`kind${languageSuffix}`].toUpperCase()}
                          </Link>
                        ))}
                      </Fragment>
                    )}
                  </div>
                );
              })}
              <Fragment>
                <div tw="flex flex-col items-center mb-12">
                  <span tw="text-xl text-white mb-4"> PRESS KIT</span>
                  {visibleBrands.map((brand: any) => (
                    <Link
                      key={`${brand.id}-pk-responsive`}
                      to={`/b/${brand.id}/pk`}
                      css={[
                        tw`mb-2 outline-none text-base text-gray-500 border-b-2 border-transparent`,
                        pathname.includes(brand.id) && tw`text-white border-white`,
                      ]}
                    >
                      {brand[`name${languageSuffix}`].toUpperCase()}
                    </Link>
                  ))}
                </div>
              </Fragment>
              <Fragment>
                <div tw="flex flex-col items-center mb-12">
                  <div tw="flex space-x-4">
                    <button
                      key="en-respnsive"
                      css={[
                        tw`mb-2 outline-none text-base text-gray-500 border-b-2 border-transparent`,
                        i18n.language === "en" && tw`text-white border-white`,
                      ]}
                      onClick={() => i18n.changeLanguage("en")}
                    >
                      ENG
                    </button>
                    <button
                      key="it-respnsive"
                      css={[
                        tw`mb-2 outline-none text-base text-gray-500 border-b-2 border-transparent`,
                        i18n.language === "it" && tw`text-white border-white`,
                      ]}
                      onClick={() => i18n.changeLanguage("it")}
                    >
                      ITA
                    </button>
                  </div>
                </div>
              </Fragment>
            </div>
          </div>
        )}
        <div tw="h-16 flex justify-between px-6 items-center">
          <button
            tw="lg:hidden block py-1 outline-none text-gray-900 border-gray-900 mr-2"
            onClick={() => setMobileMenuOpened((state) => (state !== false ? false : "menu"))}
            ref={refHandler}
            key="open-menu"
          >
            <FontAwesomeIcon icon={faBars} tw="mx-2" />
          </button>

          <Link key="logo" to="/" tw="self-center lg:w-1/4 w-1/2">
            <img tw="h-6" src="/images/header-logo.png" alt="PGN Showroom" />
          </Link>

          <div tw="lg:flex lg:justify-around lg:items-center lg:text-gray-500 lg:w-1/2 hidden">
            {visibleBrands.map((brand: any) => {
              const currentSeasons: any = filter(brand.seasons, { isEnabled: true, isStock: false });
              const stockSeasons: any = filter(brand.seasons, { isEnabled: true, isStock: true });
              const hasStockSeasons = stockSeasons.length > 0;

              return (
                <div key={brand.code} tw="flex space-x-4">
                  <div>
                    {currentSeasons.length > 0 && (
                      <span
                        tw="cursor-pointer py-1 text-gray-900"
                        onClick={() => setNavOpened((state) => (state !== false ? false : brand.code))}
                        ref={refHandler}
                      >
                        {brand[`name${languageSuffix}`].toUpperCase()}{" "}
                        {currentSeasons[0][`name${languageSuffix}`].toUpperCase()}
                      </span>
                    )}
                    {navDropdownOpened === brand.code && (
                      <div
                        ref={refCollapsable}
                        tw="flex flex-col bg-white h-auto py-1 px-2 shadow-lg z-50 absolute mt-6 w-24"
                      >
                        {currentSeasons.map((season: any) => (
                          <Link
                            key={`${brand.code}-${season.code}`}
                            to={`/b/${brand.id}/s/${season.id}`}
                            css={[
                              tw`py-1 outline-none text-gray-500 hover:(text-gray-900 border-gray-900) border-b-2 border-transparent`,
                              pathname.includes(brand.id) &&
                                pathname.includes(season.id) &&
                                tw`text-gray-900 border-gray-900`,
                            ]}
                          >
                            {season[`kind${languageSuffix}`].toUpperCase()}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                  {hasStockSeasons && (
                    <div>
                      <span
                        tw="cursor-pointer py-1 text-gray-900"
                        onClick={() => setNavOpened((state) => (state !== false ? false : `${brand.code}-stock`))}
                        ref={refHandler}
                      >
                        {stockSeasons[0][`name${languageSuffix}`].toUpperCase()}
                      </span>
                      {navDropdownOpened === `${brand.code}-stock` && (
                        <div
                          ref={refCollapsable}
                          tw="flex flex-col bg-white h-auto py-1 px-2 shadow-lg z-50 absolute mt-6 w-24"
                        >
                          {stockSeasons.map((season: any) => (
                            <Link
                              key={`${brand.code}-${season.code}-stock`}
                              to={`/b/${brand.id}/s/${season.id}`}
                              css={[
                                tw`py-1 outline-none text-gray-500 hover:(text-gray-900 border-gray-900) border-b-2 border-transparent`,
                                pathname.includes(brand.id) &&
                                  pathname.includes(season.id) &&
                                  tw`text-gray-900 border-gray-900`,
                              ]}
                            >
                              {season[`kind${languageSuffix}`].toUpperCase()}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div tw="flex space-x-8 items-center text-gray-500 lg:w-1/4 w-1/2 justify-end">
            <div tw="lg:flex hidden">
              <span
                tw="cursor-pointer py-1 text-gray-900"
                onClick={() => setNavOpened((state) => (state !== false ? false : "press-kit"))}
                ref={refHandler}
              >
                PRESS KIT
              </span>
              {navDropdownOpened === "press-kit" && (
                <div
                  ref={refCollapsable}
                  tw="flex flex-col bg-white h-auto py-1 px-2 shadow-lg z-50 absolute mt-12 w-24"
                >
                  {visibleBrands.map((brand: any) => (
                    <Link
                      key={`${brand.code}-pk`}
                      to={`/b/${brand.id}/pk`}
                      css={[
                        tw`py-1 outline-none text-gray-500 hover:(text-gray-900 border-gray-900) border-b-2 border-transparent`,
                        pathname.includes(brand.id) && tw`text-gray-900 border-gray-900`,
                      ]}
                    >
                      {brand[`name${languageSuffix}`].toUpperCase()}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div tw="lg:flex space-x-4 hidden">
              <button
                css={[
                  tw`py-1 outline-none border-b-2 border-transparent focus:(outline-none) hover:(text-gray-900 border-gray-900)`,
                  i18n.language === "en" && tw`text-gray-900 border-gray-900`,
                ]}
                onClick={() => i18n.changeLanguage("en")}
              >
                ENG
              </button>
              <button
                css={[
                  tw`py-1 outline-none border-b-2 border-transparent focus:(outline-none) hover:(text-gray-900 border-gray-900)`,
                  i18n.language === "it" && tw`text-gray-900 border-gray-900`,
                ]}
                onClick={() => i18n.changeLanguage("it")}
              >
                ITA
              </button>
            </div>

            <div tw="flex space-x-2 justify-end">
              <a
                href="/cart"
                css={
                  cartIsEmpty
                    ? [
                        tw`py-1 outline-none border-b-2 border-transparent hover:(text-gray-900 border-gray-900)`,
                        cartPathMatch !== null && tw`text-gray-900 border-gray-900`,
                      ]
                    : [
                        tw`py-1 outline-none border-b-2 text-green-500 border-green-500 hover:(text-gray-900 border-gray-900)`,
                        cartPathMatch !== null && tw`text-gray-900 border-gray-900`,
                      ]
                }
              >
                <FontAwesomeIcon icon={faShoppingCart} tw="mx-2" />
              </a>
              <button
                onClick={() => setNavOpened((state) => (state !== false ? false : "user"))}
                ref={refHandler}
                css={[
                  tw`py-1 focus:(outline-none) border-b-2 border-transparent hover:(text-gray-900 border-gray-900)`,
                  profilePathMatch !== null && tw`text-gray-900 border-gray-900`,
                ]}
              >
                <FontAwesomeIcon icon={faUser} tw="mx-2" />
              </button>
            </div>
          </div>

          {navDropdownOpened === "user" && (
            <div
              ref={refCollapsable}
              tw="flex flex-col absolute bg-white h-auto p-1 shadow-lg z-50"
              style={{ top: "4.25rem", right: "1rem" }}
            >
              <Link
                to="/profile"
                css={[
                  tw`p-2 block outline-none border-l-2 border-transparent hover:(text-gray-900 border-gray-900)`,
                  profilePathMatch !== null && tw`text-gray-900 border-gray-900`,
                ]}
              >
                {i18n.language === "it" ? "Il tuo profilo" : "Your profile"}
              </Link>
              <Link
                to="/orders"
                css={[
                  tw`p-2 block outline-none border-l-2 border-transparent hover:(text-gray-900 border-gray-900)`,
                  profilePathMatch !== null && tw`text-gray-900 border-gray-900`,
                ]}
              >
                {i18n.language === "it" ? "I tuoi Ordini" : "Your orders"}
              </Link>
              {currentUser.id !== user.id && (user.role === "SALESPERSON" || "ADMIN") ? (
                <button
                  onClick={signOutImporsonator}
                  tw="p-2 text-left block focus:(outline-none) border-l-2 text-red-400 border-transparent hover:(text-red-700 border-red-700)"
                >
                  {i18n.language === "it" ? "Lista Clienti" : "Customers List"}
                </button>
              ) : (
                <button
                  onClick={signOutUser}
                  tw="p-2 text-left block focus:(outline-none) border-l-2 text-red-400 border-transparent hover:(text-red-700 border-red-700)"
                >
                  {i18n.language === "it" ? "Esci" : "LogOut"}
                </button>
              )}
            </div>
          )}
        </div>

        <div tw="pt-16 -mt-16 min-h-screen">
          <Outlet />
        </div>
      </div>

      <ToastContainer />
    </Fragment>
  );
};
